import { useEffect, useState } from "react";
import api from "../../lib/api";
import { HEALTH_LABELS, OVERRIDE_CHOICES, STATUS_ACTIONS, customerErrorMessage, formatDay, reopenTarget, todayIso } from "../../lib/customers";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { isSessionRequired } from "../../lib/jamoraAdmin";

const REASON_MIN = 3;
const REASON_MAX = 200;
const NONE = "__none__";

// One request at a time; the server's message (rule refused, version conflict) is shown in the dialog itself.
function useSubmit(onDone) {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const send = async (request, fallback) => {
    if (sending) return;
    setSending(true);
    setError("");
    try {
      const { data } = await request();
      onDone(data);
    } catch (e) {
      setError(customerErrorMessage(e, fallback));
    } finally {
      setSending(false);
    }
  };
  return { sending, error, send, reset: () => setError("") };
}

function Footer({ onCancel, sending, disabled, label, danger = false, testid }) {
  return (
    <DialogFooter>
      <Button variant="outline" onClick={onCancel} disabled={sending}>Annuler</Button>
      <Button variant={danger ? "destructive" : "default"} type="submit" form={testid} disabled={disabled || sending}
        data-testid={`${testid}-submit`}>
        {sending && <Loader2 size={14} className="mr-1.5 animate-spin" />}{label}
      </Button>
    </DialogFooter>
  );
}

function ErrorLine({ error }) {
  if (!error) return null;
  return <p className="text-sm text-red-700" role="alert">{error}</p>;
}

function ReasonField({ id, value, onChange, required = true, label = "Motif" }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}{required ? " *" : ""}</Label>
      <Textarea id={id} rows={3} maxLength={REASON_MAX} value={value} onChange={(e) => onChange(e.target.value)} className="text-base md:text-sm"
        data-testid={id} />
      <p className="text-xs text-slate-500">{REASON_MIN} à {REASON_MAX} caractères.</p>
    </div>
  );
}

// « Déclarer la mise en service » — also the administrator's correction of that date (with a reason).
export function ActivationDialog({ account, open, onOpenChange, onDone, correction = false }) {
  const [day, setDay] = useState("");
  const [reason, setReason] = useState("");
  const req = useSubmit(onDone);
  useEffect(() => {
    if (!open || !account) return;
    setDay(correction ? account.service_activated_at || "" : todayIso());
    setReason("");
    req.reset();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!account) return null;
  const testid = correction ? "customer-correction-dialog" : "customer-activation-dialog";
  const valid = !!day && day <= todayIso() && (!correction || reason.trim().length >= REASON_MIN);
  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    const path = correction ? "activation/correction" : "activation";
    req.send(() => api.post(`/customers/${account.prospect_id}/${path}`, {
      expected_version: account.version, service_activated_at: day, ...(correction ? { reason: reason.trim() } : {}),
    }), "Impossible d'enregistrer la mise en service.");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{correction ? "Corriger la date de mise en service" : "Déclarer la mise en service"}</DialogTitle>
          <DialogDescription>
            {correction
              ? "La date réelle à laquelle la pharmacie a commencé à utiliser Jamora. La correction est conservée dans l'historique."
              : "La pharmacie utilise réellement Jamora : le dossier passe « En service » et « Actif ». Cette déclaration est définitive."}
          </DialogDescription>
        </DialogHeader>
        <form id={testid} onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="customer-activation-date">Date de mise en service *</Label>
            <Input id="customer-activation-date" type="date" max={todayIso()} value={day} onChange={(e) => setDay(e.target.value)}
              className="text-base md:text-sm" data-testid="customer-activation-date" />
            {account.commercial_won_at && <p className="text-xs text-slate-500">Pas avant le gain commercial ({formatDay(account.commercial_won_at)}), ni dans le futur.</p>}
          </div>
          {correction && <ReasonField id="customer-correction-reason" value={reason} onChange={setReason} />}
          <ErrorLine error={req.error} />
        </form>
        <Footer onCancel={() => onOpenChange(false)} sending={req.sending} disabled={!valid} testid={testid}
          label={correction ? "Corriger" : "Déclarer la mise en service"} />
      </DialogContent>
    </Dialog>
  );
}

// Suspend, resume, terminate, renounce, reopen: always a reason, a date for the two closings.
export function StatusDialog({ account, action, open, onOpenChange, onDone }) {
  const meta = STATUS_ACTIONS[action] || {};
  const [reason, setReason] = useState("");
  const [day, setDay] = useState("");
  const req = useSubmit(onDone);
  useEffect(() => {
    if (!open) return;
    setReason("");
    setDay(todayIso());
    req.reset();
  }, [open, action]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!account || !action) return null;
  const valid = reason.trim().length >= REASON_MIN && (!meta.date || (!!day && day <= todayIso()));
  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    req.send(() => api.post(`/customers/${account.prospect_id}/status`, {
      expected_version: account.version, to: meta.to || reopenTarget(account), reason: reason.trim(), ...(meta.date ? { effective_date: day } : {}),
    }), "Impossible de changer le statut du dossier.");
  };
  const testid = `customer-status-dialog-${action}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{meta.title} ?</DialogTitle>
          <DialogDescription>{meta.help}</DialogDescription>
        </DialogHeader>
        <form id={testid} onSubmit={submit} className="space-y-4">
          {meta.date && (
            <div className="space-y-2">
              <Label htmlFor="customer-status-date">Date de fin *</Label>
              <Input id="customer-status-date" type="date" max={todayIso()} value={day} onChange={(e) => setDay(e.target.value)}
                className="text-base md:text-sm" data-testid="customer-status-date" />
            </div>
          )}
          <ReasonField id="customer-status-reason" value={reason} onChange={setReason} />
          <ErrorLine error={req.error} />
        </form>
        <Footer onCancel={() => onOpenChange(false)} sending={req.sending} disabled={!valid} label={meta.button} danger={meta.danger} testid={testid} />
      </DialogContent>
    </Dialog>
  );
}

// « Modifier » : account manager, contract signature, next review. Only what changed is sent.
export function EditAccountDialog({ account, open, onOpenChange, onDone }) {
  const [managers, setManagers] = useState(null);
  const [manager, setManager] = useState(NONE);
  const [signed, setSigned] = useState("");
  const [review, setReview] = useState("");
  const [reason, setReason] = useState("");
  const req = useSubmit(onDone);

  useEffect(() => {
    if (!open || !account) return;
    setManager(account.account_manager?.id || NONE);
    setSigned(account.contract_signed_at || "");
    setReview(account.next_review_date || "");
    setReason("");
    req.reset();
    api.get("/customers/manager-options").then(({ data }) => setManagers(data)).catch(() => setManagers([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!account) return null;

  const changes = {};
  const managerId = manager === NONE ? null : manager;
  if (managerId !== (account.account_manager?.id || null)) changes.account_manager_user_id = managerId;
  if ((signed || null) !== (account.contract_signed_at || null)) changes.contract_signed_at = signed || null;
  if ((review || null) !== (account.next_review_date || null)) changes.next_review_date = review || null;
  // Correcting a signature date already recorded needs a reason (D-19); a first entry does not.
  const needsReason = "contract_signed_at" in changes && !!account.contract_signed_at;
  const valid = Object.keys(changes).length > 0 && (!signed || signed <= todayIso()) && (!needsReason || reason.trim().length >= REASON_MIN);
  const options = managers || [];
  const current = account.account_manager;
  const showCurrent = current && !options.some((o) => o.id === current.id);

  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    req.send(() => api.patch(`/customers/${account.prospect_id}`, {
      expected_version: account.version, ...changes, ...(reason.trim() ? { reason: reason.trim() } : {}),
    }), "Impossible d'enregistrer les modifications.");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="customer-edit-dialog">
        <DialogHeader>
          <DialogTitle>Modifier le dossier client</DialogTitle>
          <DialogDescription>Responsable du compte, signature du contrat et prochaine revue.</DialogDescription>
        </DialogHeader>
        <form id="customer-edit-dialog" onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="customer-edit-manager">Responsable du compte</Label>
            <Select value={manager} onValueChange={setManager} disabled={managers === null}>
              <SelectTrigger id="customer-edit-manager" data-testid="customer-edit-manager"><SelectValue placeholder="Chargement…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Aucun responsable</SelectItem>
                {showCurrent && <SelectItem value={current.id} disabled>{current.name} (ne peut plus être choisi)</SelectItem>}
                {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">Un compte actif qui a accès aux dossiers clients.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="customer-edit-signed">Signature du contrat</Label>
              <Input id="customer-edit-signed" type="date" max={todayIso()} value={signed} onChange={(e) => setSigned(e.target.value)}
                className="text-base md:text-sm" data-testid="customer-edit-signed" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customer-edit-review">Prochaine revue</Label>
              <Input id="customer-edit-review" type="date" value={review} onChange={(e) => setReview(e.target.value)}
                className="text-base md:text-sm" data-testid="customer-edit-review" />
            </div>
          </div>
          {needsReason && <ReasonField id="customer-edit-reason" value={reason} onChange={setReason} label="Motif de la correction" />}
          <ErrorLine error={req.error} />
        </form>
        <Footer onCancel={() => onOpenChange(false)} sending={req.sending} disabled={!valid} label="Enregistrer" testid="customer-edit-dialog" />
      </DialogContent>
    </Dialog>
  );
}

// Administrator only: link the pharmacy to its Jamora account, or close the open link with a reason. Jamora's registry is only READ: the
// suggestions come from it (name, e-mail, phone compared with the file), a link is never made without a click, and it becomes « verified »
// only when an e-mail or a phone number matches — a name alone never proves it.
const SIGNAL_LABELS = { email: "Courriel identique", phone: "Téléphone identique", name_exact: "Même nom", name_close: "Nom proche" };

function TenantSuggestions({ account, open, sending, onPick }) {
  const [state, setState] = useState({ status: "loading", items: [] });
  useEffect(() => {
    if (!open || !account) return undefined;
    let cancelled = false;
    setState({ status: "loading", items: [] });
    api.get(`/jamora-admin/customers/${account.prospect_id}/tenant-suggestions`)
      .then(({ data }) => !cancelled && setState({ status: "ok", items: data.items || [] }))
      .catch((e) => !cancelled && setState({ status: isSessionRequired(e) ? "session" : e?.response?.status === 404 ? "off" : "error", items: [] }));
    return () => { cancelled = true; };
  }, [open, account?.prospect_id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (state.status === "loading") return <p className="text-sm text-slate-500" data-testid="tenant-suggestions-loading">Recherche dans Jamora…</p>;
  if (state.status === "session") {
    return (
      <p className="text-sm text-slate-600" data-testid="tenant-suggestions-session">
        Pour voir les pharmacies Jamora proches de ce dossier, <Link to="/jamora-admin" className="underline">ouvrez l'administration Jamora</Link> (mot
        de passe), puis revenez ici. Vous pouvez aussi saisir l'identifiant à la main.
      </p>
    );
  }
  if (state.status !== "ok") {
    return <p className="text-sm text-slate-500" data-testid="tenant-suggestions-off">Les suggestions ne sont pas disponibles pour le moment : saisissez l'identifiant à la main.</p>;
  }
  if (state.items.length === 0) {
    return <p className="text-sm text-slate-500" data-testid="tenant-suggestions-empty">Aucune pharmacie Jamora ne ressemble à ce dossier. Saisissez l'identifiant à la main si vous le connaissez.</p>;
  }
  return (
    <ul className="space-y-2" data-testid="tenant-suggestions">
      {state.items.map((item) => (
        <li key={item.schema} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 p-3" data-testid={`tenant-suggestion-${item.schema}`}>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{item.name}{item.demo ? " (démonstration)" : ""}{item.active === false ? " (inactive)" : ""}</p>
            <p className="text-xs text-slate-500 font-mono truncate">{item.schema}</p>
            <p className="mt-1 flex flex-wrap gap-1">
              {item.reasons.map((r) => (
                <span key={r} className={`rounded-full px-2 py-0.5 text-2xs font-medium ${r === "email" || r === "phone" ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>
                  {SIGNAL_LABELS[r] || r}
                </span>
              ))}
            </p>
          </div>
          <Button type="button" size="sm" variant="outline" disabled={sending} onClick={() => onPick(item)} data-testid={`tenant-pick-${item.schema}`}>Lier</Button>
        </li>
      ))}
    </ul>
  );
}

export function TenantDialog({ account, open, onOpenChange, onDone }) {
  const link = account?.tenant_link;
  const [ref, setRef] = useState("");
  const [reason, setReason] = useState("");
  const [manual, setManual] = useState(false);
  const req = useSubmit(onDone);
  useEffect(() => {
    if (!open) return;
    setRef("");
    setReason("");
    setManual(false);
    req.reset();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!account) return null;
  const closing = !!link;
  const base = `/customers/${account.prospect_id}`;
  const valid = closing ? reason.trim().length >= REASON_MIN : /^[a-z0-9_]{1,63}$/.test(ref.trim().toLowerCase());
  // Linking then verifying is two server steps; a verification that fails leaves the link « declared » (and the file says so).
  const linkThenVerify = (schema, verifiable) => req.send(async () => {
    const linked = await api.post(`${base}/tenant-link`, { tenant_ref: schema });
    if (!verifiable) return linked;
    try {
      return await api.post(`/jamora-admin${base}/tenant-link/verify`);
    } catch {
      return linked;
    }
  }, "Impossible de modifier la liaison Jamora.");
  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    if (closing) req.send(() => api.delete(`${base}/tenant-link`, { data: { reason: reason.trim() } }), "Impossible de modifier la liaison Jamora.");
    else linkThenVerify(ref.trim().toLowerCase(), true);
  };
  const verify = () => req.send(() => api.post(`/jamora-admin${base}/tenant-link/verify`), "Impossible de vérifier la liaison.");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="customer-tenant-dialog">
        <DialogHeader>
          <DialogTitle>{closing ? "Compte Jamora lié" : "Lier le compte Jamora"}</DialogTitle>
          <DialogDescription>
            {closing
              ? "Vous pouvez vérifier la liaison avec Jamora, ou la fermer : elle est alors conservée dans l'historique. Rien ne change dans Jamora."
              : "Choisissez la pharmacie Jamora de ce client. La liaison est vérifiée si son courriel ou son téléphone correspond. Rien ne change dans Jamora."}
          </DialogDescription>
        </DialogHeader>
        <form id="customer-tenant-dialog" onSubmit={submit} className="space-y-4">
          {closing ? (
            <>
              <p className="text-sm text-slate-700" data-testid="customer-tenant-current">
                <span className="font-mono">{link.tenant_ref}</span> — {link.state === "verified" ? "vérifié" : "déclaré, non vérifié"}
              </p>
              {link.state !== "verified" && (
                <Button type="button" variant="outline" size="sm" onClick={verify} disabled={req.sending} data-testid="customer-tenant-verify">
                  Vérifier avec Jamora
                </Button>
              )}
              <ReasonField id="customer-tenant-reason" value={reason} onChange={setReason} label="Motif pour délier" />
            </>
          ) : (
            <>
              <TenantSuggestions account={account} open={open} sending={req.sending} onPick={(item) => linkThenVerify(item.schema, item.verifiable)} />
              {manual ? (
                <div className="space-y-2">
                  <Label htmlFor="customer-tenant-ref">Identifiant du compte Jamora *</Label>
                  <Input id="customer-tenant-ref" value={ref} onChange={(e) => setRef(e.target.value)} maxLength={63} autoComplete="off"
                    className="text-base md:text-sm font-mono" data-testid="customer-tenant-ref" />
                  <p className="text-xs text-slate-500">Lettres minuscules, chiffres et « _ » (63 caractères au plus).</p>
                </div>
              ) : (
                <button type="button" className="text-sm text-slate-600 underline" onClick={() => setManual(true)} data-testid="customer-tenant-manual">
                  Saisir l'identifiant à la main
                </button>
              )}
            </>
          )}
          <ErrorLine error={req.error} />
        </form>
        {(closing || manual) && (
          <Footer onCancel={() => onOpenChange(false)} sending={req.sending} disabled={!valid} label={closing ? "Délier" : "Lier"} danger={closing}
            testid="customer-tenant-dialog" />
        )}
      </DialogContent>
    </Dialog>
  );
}

// « Corriger la santé » (D-18): a state and a reason; the computed health stays shown beside it. « Lever la correction » removes it.
export function HealthDialog({ account, open, onOpenChange, onDone }) {
  const current = account?.health?.override;
  const [state, setState] = useState("");
  const [reason, setReason] = useState("");
  const req = useSubmit(onDone);
  useEffect(() => {
    if (!open || !account) return;
    setState(current?.state || "");
    setReason("");
    req.reset();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!account) return null;
  const valid = !!state && reason.trim().length >= REASON_MIN;
  const send = (body) => req.send(() => api.put(`/customers/${account.prospect_id}/health-override`, { expected_version: account.version, ...body }),
    "Impossible d'enregistrer la santé.");
  const submit = (e) => {
    e.preventDefault();
    if (valid) send({ state, reason: reason.trim() });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="customer-health-dialog">
        <DialogHeader>
          <DialogTitle>Corriger la santé du dossier</DialogTitle>
          <DialogDescription>
            Santé calculée : {HEALTH_LABELS[account.health?.computed] || "—"}. Votre correction s'affiche à sa place, avec votre nom et votre
            motif, jusqu'à ce qu'elle soit levée.
          </DialogDescription>
        </DialogHeader>
        <form id="customer-health-dialog" onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="customer-health-state">Santé *</Label>
            <Select value={state} onValueChange={setState}>
              <SelectTrigger id="customer-health-state" data-testid="customer-health-state"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>
                {OVERRIDE_CHOICES.map((s) => <SelectItem key={s} value={s}>{HEALTH_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <ReasonField id="customer-health-reason" value={reason} onChange={setReason} />
          {current && (
            <Button type="button" variant="outline" className="w-full" onClick={() => send({ state: null })} disabled={req.sending}
              data-testid="customer-health-lift">
              Lever la correction (revenir à la santé calculée)
            </Button>
          )}
          <ErrorLine error={req.error} />
        </form>
        <Footer onCancel={() => onOpenChange(false)} sending={req.sending} disabled={!valid} label="Enregistrer" testid="customer-health-dialog" />
      </DialogContent>
    </Dialog>
  );
}

// An objective of the customer follow-up: label and optional due date (creation and edition).
export function ObjectiveDialog({ account, objective, open, onOpenChange, onDone }) {
  const [label, setLabel] = useState("");
  const [due, setDue] = useState("");
  const req = useSubmit(onDone);
  useEffect(() => {
    if (!open) return;
    setLabel(objective?.label || "");
    setDue(objective?.due_date || "");
    req.reset();
  }, [open, objective?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!account) return null;
  const valid = label.trim().length >= 3 && (!objective || label.trim() !== objective.label || (due || null) !== (objective.due_date || null));
  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    req.send(() => (objective
      ? api.patch(`/customers/${account.prospect_id}/objectives/${objective.id}`, {
        expected_version: objective.version,
        ...(label.trim() !== objective.label ? { label: label.trim() } : {}),
        ...((due || null) !== (objective.due_date || null) ? { due_date: due || null } : {}),
      })
      : api.post(`/customers/${account.prospect_id}/objectives`, { label: label.trim(), due_date: due || null })), "Impossible d'enregistrer l'objectif.");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="customer-objective-dialog">
        <DialogHeader>
          <DialogTitle>{objective ? "Modifier l'objectif" : "Ajouter un objectif"}</DialogTitle>
          <DialogDescription>Ce que le suivi de cette pharmacie doit obtenir, par exemple « Former les deux vendeurs au module stock ».</DialogDescription>
        </DialogHeader>
        <form id="customer-objective-dialog" onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="customer-objective-label">Objectif *</Label>
            <Input id="customer-objective-label" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={200}
              className="text-base md:text-sm" data-testid="customer-objective-label" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="customer-objective-due">Échéance</Label>
            <Input id="customer-objective-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} className="text-base md:text-sm"
              data-testid="customer-objective-due" />
          </div>
          <ErrorLine error={req.error} />
        </form>
        <Footer onCancel={() => onOpenChange(false)} sending={req.sending} disabled={!valid} label={objective ? "Enregistrer" : "Ajouter"}
          testid="customer-objective-dialog" />
      </DialogContent>
    </Dialog>
  );
}
